/* Ormonde Jayne — fragrance filter drawer
   Bar behaviour (pinned line, active label), the drawer (groups, draft
   selection, live counts through the Section Rendering API, apply, confirm
   step, focus and keyboard), chips, load more and URL state, plus the
   shop-all format groups (88ml, 10ml, Travel Labs, candles, room sprays,
   diffusers): a theme-managed `formats` URL param that hides the groups
   outside the selection, with Show all per group.
   Vanilla JS, no jQuery. */

(function () {
  'use strict';

  var doc = document;
  var bar = doc.querySelector('[data-oj-filter-bar]');
  var drawer = doc.querySelector('[data-oj-filter-drawer]');
  var backdrop = doc.querySelector('[data-oj-filter-backdrop]');
  var results = doc.querySelector('[data-oj-results]');
  if (!bar && !drawer && !results) return;

  var PARAM_PREFIX = 'filter.p.m.fragrance.';
  var FORMAT_PARAM = 'formats';
  var SESSION_KEY = 'oj-filter-expanded';
  var GROUP_ORDER = ['family', 'format', 'notes', 'character', 'wear', 'season', 'collection'];
  function isFilterKey(k) { return k.indexOf(PARAM_PREFIX) === 0 || k === FORMAT_PARAM; }
  var reduceMotion = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  var mobileMQ = window.matchMedia ? window.matchMedia('(max-width: 767px)') : null;
  function isMobile() { return !!(mobileMQ && mobileMQ.matches); }

  /* ---------- Strings (from the locale files, emitted by the drawer snippet) ---------- */

  var strings = {};
  var stringsEl = doc.querySelector('[data-oj-filter-strings]');
  if (stringsEl) {
    try { strings = JSON.parse(stringsEl.textContent); } catch (e) { strings = {}; }
  }
  function fmt(tpl, n) { return String(tpl || '').replace('__N__', n); }
  function fmtLabel(tpl, l) { return String(tpl || '').replace('__L__', l); }
  function plural(n, oneKey, otherKey) { return n === 1 ? (strings[oneKey] || '') : fmt(strings[otherKey], n); }
  function showLabel(n) { return plural(n, 'showOne', 'showOther'); }
  function fragrancesLabel(n) { return plural(n, 'fragrancesOne', 'fragrancesOther'); }
  function selectedLabel(n) { return plural(n, 'selectedOne', 'selectedOther'); }
  function filterLabel(n) { return n > 0 ? fmt(strings.filterCount, n) : (strings.filter || 'Filter'); }

  /* ---------- Analytics hook (destinations are wired in the analytics module) ---------- */

  function track(name, data) {
    try {
      doc.dispatchEvent(new CustomEvent('oj:filter:track', { detail: { event: name, data: data || {} } }));
    } catch (e) { /* ignore */ }
  }

  /* ---------- URL state ---------- */

  function readState(search) {
    var state = {};
    var params = new URLSearchParams(search === undefined ? window.location.search : search);
    params.forEach(function (value, key) {
      if (!isFilterKey(key)) return;
      if (!state[key]) state[key] = [];
      value.split(',').forEach(function (v) {
        if (v && state[key].indexOf(v) === -1) state[key].push(v);
      });
    });
    return state;
  }

  function cloneState(s) {
    var c = {};
    Object.keys(s).forEach(function (k) { if (s[k].length) c[k] = s[k].slice(); });
    return c;
  }

  function stateCount(s) {
    var n = 0;
    Object.keys(s).forEach(function (k) { n += s[k].length; });
    return n;
  }

  function stateEquals(a, b) {
    var ka = Object.keys(a).filter(function (k) { return a[k].length; }).sort();
    var kb = Object.keys(b).filter(function (k) { return b[k].length; }).sort();
    if (ka.join('|') !== kb.join('|')) return false;
    return ka.every(function (k) {
      return a[k].slice().sort().join(',') === b[k].slice().sort().join(',');
    });
  }

  /* Builds the query string for a state, keeping unrelated params (locale
     previews, sort) and dropping page and the old fragrance params. */
  function buildSearch(state, extra) {
    var params = new URLSearchParams(window.location.search);
    var drop = [];
    params.forEach(function (v, k) {
      if (isFilterKey(k) || k === 'page' || k === 'section_id' || k === 'sections') drop.push(k);
    });
    drop.forEach(function (k) { params.delete(k); });
    Object.keys(state).forEach(function (k) {
      state[k].forEach(function (v) { params.append(k, v); });
    });
    if (extra) Object.keys(extra).forEach(function (k) { params.set(k, extra[k]); });
    var s = params.toString();
    return s ? '?' + s : '';
  }

  function stateUrl(state) {
    return window.location.pathname + buildSearch(state);
  }

  /* ---------- Bar ---------- */

  var nav = doc.querySelector('nav.main-header-nav');
  var barTop = 0;
  var ticking = false;
  var settleTimer = null;
  var filterControl = bar ? bar.querySelector('[data-oj-filter-open="filter"]') : null;

  function measureTop() {
    var t = 0;
    if (nav) t = Math.max(0, Math.round(nav.getBoundingClientRect().bottom));
    if (bar && t !== barTop) {
      barTop = t;
      bar.style.setProperty('--oj-filter-bar-top', barTop + 'px');
    }
    return barTop;
  }

  function checkPinned() {
    ticking = false;
    if (!bar) return;
    var top = measureTop();
    var r = bar.getBoundingClientRect();
    var parentBottom = bar.parentNode ? bar.parentNode.getBoundingClientRect().bottom : Infinity;
    var pinned = r.top <= top + 0.5 && parentBottom > r.bottom + 1;
    bar.classList.toggle('is-pinned', pinned);
  }

  function onScroll() {
    if (!ticking) {
      ticking = true;
      window.requestAnimationFrame(checkPinned);
    }
    window.clearTimeout(settleTimer);
    settleTimer = window.setTimeout(checkPinned, 320);
  }

  if (bar) {
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    window.addEventListener('load', checkPinned);
    if (nav) nav.addEventListener('transitionend', checkPinned);
    checkPinned();
  }

  function updateBar(state, total) {
    if (!bar) return;
    var n = stateCount(state);
    var label = bar.querySelector('[data-oj-filter-label]');
    if (label) label.textContent = filterLabel(n);
    if (filterControl) filterControl.classList.toggle('is-active', n > 0);
    var count = bar.querySelector('[data-oj-filter-bar-count]');
    if (count) {
      count.textContent = fragrancesLabel(total);
      count.hidden = n === 0;
    }
    bar.setAttribute('data-oj-filter-active', String(n));
  }

  /* ---------- Open hooks ---------- */

  function bindOpeners(root) {
    var buttons = (root || doc).querySelectorAll('[data-oj-filter-open]');
    Array.prototype.forEach.call(buttons, function (btn) {
      if (btn.__ojBound) return;
      btn.__ojBound = true;
      btn.addEventListener('click', function (event) {
        event.preventDefault();
        openDrawer(btn.getAttribute('data-oj-filter-open') || 'filter', btn);
      });
    });
  }

  /* ---------- Drawer state ---------- */

  var applied = readState();
  var draft = cloneState(applied);
  var lastTotal = results ? parseInt(results.getAttribute('data-oj-total'), 10) || 0 : 0;
  var drawerOpen = false;
  var confirming = false;
  var pushedHistory = false;
  var lastTrigger = null;
  var confirmDiscard = !!(drawer && drawer.getAttribute('data-oj-confirm-discard') === 'true');
  var openAllDesktop = !!(drawer && drawer.getAttribute('data-oj-open-all-desktop') === 'true');
  var form = drawer ? drawer.querySelector('[data-oj-filter-form]') : null;

  function groups() {
    return drawer ? Array.prototype.slice.call(drawer.querySelectorAll('[data-oj-group]')) : [];
  }

  function checkboxes(root) {
    return Array.prototype.slice.call((root || drawer).querySelectorAll('[data-oj-checkbox]'));
  }

  function setGroupOpen(section, open) {
    var toggle = section.querySelector('[data-oj-group-toggle]');
    var panel = section.querySelector('.oj-filter-group__panel');
    if (!toggle || !panel) return;
    section.classList.toggle('is-open', open);
    toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    panel.hidden = !open;
    var glyph = section.querySelector('[data-oj-group-glyph]');
    if (glyph) glyph.textContent = open ? '–' : '+';
  }

  function expandedDefaults() {
    if (isMobile()) return ['family'];
    if (openAllDesktop) return GROUP_ORDER.slice();
    var remembered = null;
    try { remembered = JSON.parse(window.sessionStorage.getItem(SESSION_KEY)); } catch (e) { remembered = null; }
    if (Array.isArray(remembered)) return remembered;
    return ['family', 'format', 'character'];
  }

  function rememberExpanded() {
    if (isMobile()) return;
    var open = groups().filter(function (s) { return s.classList.contains('is-open'); })
      .map(function (s) { return s.getAttribute('data-oj-group'); });
    try { window.sessionStorage.setItem(SESSION_KEY, JSON.stringify(open)); } catch (e) { /* ignore */ }
  }

  function applyExpanded(keys) {
    groups().forEach(function (s) {
      var k = s.getAttribute('data-oj-group');
      if (k === 'family') return;
      setGroupOpen(s, keys.indexOf(k) !== -1);
    });
  }

  function readDraftFromInputs() {
    var s = {};
    checkboxes().forEach(function (input) {
      if (!input.checked) return;
      if (!s[input.name]) s[input.name] = [];
      s[input.name].push(input.value);
    });
    return s;
  }

  function syncInputs(state) {
    checkboxes().forEach(function (input) {
      var vals = state[input.name] || [];
      input.checked = vals.indexOf(input.value) !== -1;
    });
    refreshDraftUI();
  }

  function refreshDraftUI() {
    if (!drawer) return;
    groups().forEach(function (s) {
      var n = s.querySelectorAll('[data-oj-checkbox]:checked').length;
      var sel = s.querySelector('[data-oj-group-selected]');
      if (sel) {
        sel.hidden = n === 0;
        sel.textContent = selectedLabel(n);
      }
    });
    var clear = drawer.querySelector('[data-oj-filter-clear]');
    if (clear) clear.hidden = stateCount(draft) === 0;
  }

  function setApplyLabel(text) {
    var el = drawer ? drawer.querySelector('[data-oj-apply-label]') : null;
    if (el) el.textContent = text;
  }

  /* ---------- Live counting ---------- */

  var countTimer = null;
  var countController = null;

  function scheduleCount() {
    window.clearTimeout(countTimer);
    setApplyLabel(strings.counting || '');
    countTimer = window.setTimeout(runCount, 150);
  }

  function runCount() {
    if (countController) countController.abort();
    countController = window.AbortController ? new AbortController() : null;
    var url = window.location.pathname + buildSearch(draft, { section_id: 'oj-filter-counts' });
    fetch(url, { signal: countController ? countController.signal : undefined, credentials: 'same-origin' })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var m = html.match(/<script[^>]*data-oj-counts[^>]*>([\s\S]*?)<\/script>/);
        if (!m) throw new Error('counts missing');
        applyCounts(JSON.parse(m[1]));
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        setApplyLabel(showLabel(lastTotal));
      });
  }

  /* Applies per-value counts from the counts section: zero-count values are
     disabled and dimmed but never removed; ticked values are never disabled.
     Format rows read data.formats (cards per format); the total for a format
     selection is the number of distinct products behind the chosen formats. */
  function applyCounts(data) {
    var filters = data.filters || {};
    var formats = data.formats || {};
    checkboxes().forEach(function (input) {
      var count;
      if (input.name === FORMAT_PARAM) {
        count = Object.prototype.hasOwnProperty.call(formats, input.value) ? formats[input.value] : 0;
      } else {
        var group = filters[input.name];
        count = group && Object.prototype.hasOwnProperty.call(group, input.value) ? group[input.value] : 0;
      }
      setValueCount(input, count);
    });
    setApplyLabel(showLabel(formatTotal(data, draft)));
  }

  function formatTotal(data, state) {
    var sel = formatSelection(state);
    if (!sel.length || !data.members) return data.total || 0;
    var seen = {};
    var n = 0;
    sel.forEach(function (key) {
      (data.members[key] || []).forEach(function (id) {
        if (!seen[id]) { seen[id] = true; n += 1; }
      });
    });
    return n;
  }

  function setValueCount(input, count) {
    var li = input.closest('[data-oj-value]');
    if (!li) return;
    var countEl = li.querySelector('[data-oj-count]');
    if (countEl) countEl.textContent = count;
    input.setAttribute('aria-label', (li.getAttribute('data-oj-label') || input.value) + ', ' + count);
    var empty = count === 0 && !input.checked;
    input.disabled = empty;
    li.classList.toggle('is-empty', empty);
  }

  /* After an apply, mirror the freshly rendered drawer's counts and states. */
  function syncDrawerFromMarkup(newDrawer) {
    var fresh = {};
    checkboxes(newDrawer).forEach(function (input) {
      var li = input.closest('[data-oj-value]');
      var countEl = li ? li.querySelector('[data-oj-count]') : null;
      fresh[input.name + '=' + input.value] = {
        count: countEl ? parseInt(countEl.textContent, 10) || 0 : 0,
        checked: input.checked
      };
    });
    checkboxes().forEach(function (input) {
      var f = fresh[input.name + '=' + input.value];
      if (!f) return;
      /* Format rows render unchecked server side (Liquid cannot read the param); their state lives here. */
      if (input.name !== FORMAT_PARAM) input.checked = f.checked;
      setValueCount(input, f.count);
    });
    setApplyLabel(showLabel(lastTotal));
    refreshDraftUI();
  }

  /* ---------- Confirm step ---------- */

  function showConfirm() {
    var msg = drawer.querySelector('[data-oj-filter-unapplied]');
    var discard = drawer.querySelector('[data-oj-filter-discard]');
    if (msg) msg.hidden = false;
    if (discard) discard.hidden = false;
  }

  function hideConfirm() {
    if (!drawer) return;
    var msg = drawer.querySelector('[data-oj-filter-unapplied]');
    var discard = drawer.querySelector('[data-oj-filter-discard]');
    if (msg) msg.hidden = true;
    if (discard) discard.hidden = true;
  }

  /* ---------- Open / close ---------- */

  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function visibleFocusables() {
    return Array.prototype.filter.call(drawer.querySelectorAll(FOCUSABLE), function (el) {
      return el.offsetParent !== null || el === doc.activeElement;
    });
  }

  function onKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      attemptClose();
      return;
    }
    if (event.key !== 'Tab') return;
    var items = visibleFocusables();
    if (!items.length) return;
    var first = items[0];
    var last = items[items.length - 1];
    if (event.shiftKey && (doc.activeElement === first || !drawer.contains(doc.activeElement))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (doc.activeElement === last || !drawer.contains(doc.activeElement))) {
      event.preventDefault();
      first.focus();
    }
  }

  function openDrawer(source, trigger) {
    if (!drawer || drawerOpen) return;
    lastTrigger = trigger || filterControl;
    draft = cloneState(applied);
    syncInputs(draft);
    confirming = false;
    hideConfirm();

    var guided = source === 'invitation';
    var guide = drawer.querySelector('[data-oj-filter-guide]');
    var keys = expandedDefaults();
    if (guided && keys.indexOf('character') === -1) keys = keys.concat(['character']);
    applyExpanded(keys);
    if (guide) guide.hidden = !guided;
    setApplyLabel(showLabel(lastTotal));

    drawer.hidden = false;
    if (backdrop) backdrop.hidden = false;
    doc.documentElement.classList.add('oj-filter-lock');
    void drawer.offsetWidth;
    drawer.classList.add('is-open');
    if (backdrop) backdrop.classList.add('is-open');
    drawerOpen = true;
    if (filterControl) filterControl.setAttribute('aria-expanded', 'true');

    if (isMobile() && window.history && window.history.pushState) {
      window.history.pushState({ ojFilterDrawer: true }, '', window.location.href);
      pushedHistory = true;
    }

    if (form) {
      if (guided) {
        var character = drawer.querySelector('[data-oj-group="character"]');
        form.scrollTop = character ? character.offsetTop - form.offsetTop : 0;
      } else {
        form.scrollTop = 0;
      }
    }

    var close = drawer.querySelector('[data-oj-filter-close]');
    if (close) close.focus({ preventScroll: true });
    doc.addEventListener('keydown', onKeydown);
    track('filter_open', { source: source });
  }

  function closeDrawer(opts) {
    opts = opts || {};
    if (!drawer || !drawerOpen) return;
    drawerOpen = false;
    confirming = false;
    hideConfirm();
    drawer.classList.remove('is-open');
    if (backdrop) backdrop.classList.remove('is-open');
    doc.documentElement.classList.remove('oj-filter-lock');
    if (filterControl) filterControl.setAttribute('aria-expanded', 'false');
    doc.removeEventListener('keydown', onKeydown);

    var finish = function () {
      if (drawerOpen) return;
      drawer.hidden = true;
      if (backdrop) backdrop.hidden = true;
    };
    if (reduceMotion) finish(); else window.setTimeout(finish, 320);

    if (pushedHistory && !opts.fromPopstate && !opts.keepHistory) {
      pushedHistory = false;
      window.history.back();
    }
    pushedHistory = false;

    if (!opts.noFocus && lastTrigger && typeof lastTrigger.focus === 'function') {
      lastTrigger.focus({ preventScroll: true });
    }
  }

  function attemptClose() {
    if (confirmDiscard && !confirming && !stateEquals(draft, applied)) {
      confirming = true;
      showConfirm();
      return;
    }
    closeDrawer();
  }

  /* ---------- Apply: swap the results in place ---------- */

  var renderController = null;

  function setCountText(text) {
    if (!results) return;
    var el = results.querySelector('[data-oj-chips-count]');
    if (el) el.textContent = text;
  }

  function scrollToGrid() {
    if (!results) return;
    var offset = bar ? bar.offsetHeight + measureTop() : 0;
    var top = results.getBoundingClientRect().top + window.pageYOffset - offset;
    window.scrollTo({ top: Math.max(0, top), behavior: reduceMotion ? 'auto' : 'smooth' });
  }

  function renderResults(url, opts) {
    opts = opts || {};
    if (!results) { window.location.href = url; return; }
    var grid = results.querySelector('[data-oj-grid]');
    if (grid) grid.classList.add('is-loading');
    setCountText(strings.updating || '');
    if (renderController) renderController.abort();
    renderController = window.AbortController ? new AbortController() : null;
    var sectionId = results.getAttribute('data-oj-section-id');
    var fetchUrl = url + (url.indexOf('?') === -1 ? '?' : '&') + 'section_id=' + encodeURIComponent(sectionId);

    fetch(fetchUrl, { signal: renderController ? renderController.signal : undefined, credentials: 'same-origin' })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var fresh = parsed.querySelector('[data-oj-results]');
        if (!fresh) throw new Error('results missing');

        ['[data-oj-chips]', '[data-oj-grid]', '[data-oj-more]'].forEach(function (sel) {
          var oldEl = results.querySelector(sel);
          var newEl = fresh.querySelector(sel);
          if (oldEl && newEl) oldEl.innerHTML = newEl.innerHTML;
        });
        var oldEmpty = results.querySelector('[data-oj-empty]');
        if (oldEmpty) oldEmpty.parentNode.removeChild(oldEmpty);
        var newEmpty = fresh.querySelector('[data-oj-empty]');
        if (newEmpty && grid) grid.insertAdjacentElement('afterend', newEmpty);

        results.setAttribute('data-oj-total', fresh.getAttribute('data-oj-total') || '0');
        results.setAttribute('data-oj-active', fresh.getAttribute('data-oj-active') || '0');
        lastTotal = parseInt(fresh.getAttribute('data-oj-total'), 10) || 0;

        var freshDrawer = parsed.querySelector('[data-oj-filter-drawer]');
        if (freshDrawer && drawer) syncDrawerFromMarkup(freshDrawer);

        if (grid) grid.classList.remove('is-loading');
        applyFormatView(applied);
        renderFormatChips();
        updateBar(applied, visibleTotal());
        if (formatSelection(applied).length) setCountText(fragrancesLabel(visibleTotal()));
        bindOpeners(results);
        if (window.lazyLoadInstance && window.lazyLoadInstance.update) window.lazyLoadInstance.update();
        if (window.OJLazyVideo) window.OJLazyVideo.init();
        if (!opts.noScroll) scrollToGrid();
        if (!opts.noFocus && filterControl) filterControl.focus({ preventScroll: true });
        if (bar) checkPinned();
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        window.location.href = url;
      });
  }

  function applyState(nextState, opts) {
    opts = opts || {};
    applied = cloneState(nextState);
    draft = cloneState(applied);
    var url = stateUrl(applied);
    if (window.history && window.history.pushState) {
      if (opts.replace) window.history.replaceState({ ojFilter: true }, '', url);
      else window.history.pushState({ ojFilter: true }, '', url);
    }
    renderResults(url, opts);
  }

  /* ---------- Load more (next page of the same filtered set) ---------- */

  function loadMore(button) {
    var page = parseInt(button.getAttribute('data-oj-next-page'), 10);
    if (!page || !results) return;
    button.classList.add('is-loading');
    var sectionId = results.getAttribute('data-oj-section-id');
    var url = window.location.pathname + buildSearch(applied, { page: page, section_id: sectionId });
    fetch(url, { credentials: 'same-origin' })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var fresh = parsed.querySelector('[data-oj-results]');
        if (!fresh) throw new Error('results missing');
        var grid = results.querySelector('[data-oj-grid]');
        var freshGrid = fresh.querySelector('[data-oj-grid]');
        if (grid && freshGrid) {
          while (freshGrid.firstChild) grid.appendChild(freshGrid.firstChild);
        }
        var more = results.querySelector('[data-oj-more]');
        var freshMore = fresh.querySelector('[data-oj-more]');
        if (more && freshMore) more.innerHTML = freshMore.innerHTML;
        if (window.lazyLoadInstance && window.lazyLoadInstance.update) window.lazyLoadInstance.update();
        if (window.OJLazyVideo) window.OJLazyVideo.init();
      })
      .catch(function () {
        button.classList.remove('is-loading');
      });
  }

  /* ---------- Shop all: format groups ---------- */

  function knownFormats() {
    var keys = [];
    if (results) {
      var attr = results.getAttribute('data-oj-formats') || '';
      attr.split(',').forEach(function (k) { if (k && keys.indexOf(k) === -1) keys.push(k); });
      Array.prototype.forEach.call(results.querySelectorAll('[data-oj-format-group]'), function (group) {
        var k = group.getAttribute('data-oj-format-group');
        if (k && keys.indexOf(k) === -1) keys.push(k);
      });
    }
    if (drawer) {
      Array.prototype.forEach.call(drawer.querySelectorAll('[data-oj-group="format"] [data-oj-value]'), function (li) {
        var k = li.getAttribute('data-oj-value');
        if (k && keys.indexOf(k) === -1) keys.push(k);
      });
    }
    return keys;
  }

  /* The applied format keys the page actually knows; unknown values in the URL are ignored. */
  function formatSelection(state) {
    var known = knownFormats();
    return (state[FORMAT_PARAM] || []).filter(function (v) { return known.indexOf(v) !== -1; });
  }

  function expandGroup(group) {
    Array.prototype.forEach.call(group.querySelectorAll('.product-card.is-truncated'), function (card) {
      card.classList.remove('is-truncated');
    });
    var more = group.querySelector('.oj-format-group__more');
    if (more) more.hidden = true;
  }

  /* Hides the groups outside the applied format selection; a chosen format
     shows all of its cards. With nothing left on the page the format empty
     state shows (the server empty state, if any, takes precedence). */
  function applyFormatView(state) {
    if (!results) return;
    var sel = formatSelection(state);
    var shown = 0;
    Array.prototype.forEach.call(results.querySelectorAll('[data-oj-format-group]'), function (group) {
      var key = group.getAttribute('data-oj-format-group');
      var show = !sel.length || sel.indexOf(key) !== -1;
      group.hidden = !show;
      if (show) {
        shown += 1;
        if (sel.length) expandGroup(group);
      }
    });
    var empty = results.querySelector('[data-oj-format-empty]');
    if (empty) empty.hidden = !(sel.length && shown === 0 && !results.querySelector('[data-oj-empty]'));
  }

  /* Distinct products on show: the server total unless a format selection narrows the grouped grid. */
  function visibleTotal() {
    if (!results) return lastTotal;
    var sel = formatSelection(applied);
    if (!sel.length || !results.querySelector('[data-oj-format-group]')) return lastTotal;
    var seen = {};
    var n = 0;
    Array.prototype.forEach.call(results.querySelectorAll('[data-oj-format-group]:not([hidden]) .product-card[data-oj-product]'), function (card) {
      var id = card.getAttribute('data-oj-product');
      if (!seen[id]) { seen[id] = true; n += 1; }
    });
    return n;
  }

  function formatLabel(key) {
    var li = drawer ? drawer.querySelector('[data-oj-group="format"] [data-oj-value="' + key + '"]') : null;
    if (li && li.getAttribute('data-oj-label')) return li.getAttribute('data-oj-label');
    var group = results ? results.querySelector('[data-oj-format-group="' + key + '"] .oj-format-group__title') : null;
    return group ? group.textContent.trim() : key;
  }

  /* Format chips are drawn here, prefixed with the group label like the
     other prefixed groups; the Liquid row carries the fragrance chips. */
  function renderFormatChips() {
    if (!results) return;
    var row = results.querySelector('[data-oj-chips-row]');
    if (!row) return;
    var slot = row.querySelector('[data-oj-format-chips]');
    var sel = formatSelection(applied);
    var groupLabel = strings.formatGroup || 'Format';
    if (slot) {
      slot.innerHTML = '';
      sel.forEach(function (key) {
        var label = formatLabel(key);
        var btn = doc.createElement('button');
        btn.type = 'button';
        btn.className = 'oj-filter-chip';
        btn.setAttribute('data-oj-chip', '');
        btn.setAttribute('data-oj-param', FORMAT_PARAM);
        btn.setAttribute('data-oj-value', key);
        btn.setAttribute('aria-label', fmtLabel(strings.remove, label));
        var text = doc.createElement('span');
        text.className = 'oj-filter-chip__label';
        text.textContent = groupLabel + ': ' + label;
        var x = doc.createElement('span');
        x.className = 'oj-filter-chip__x';
        x.setAttribute('aria-hidden', 'true');
        x.innerHTML = '&times;';
        btn.appendChild(text);
        btn.appendChild(x);
        slot.appendChild(btn);
      });
    }
    var total = (parseInt(row.getAttribute('data-oj-chip-count'), 10) || 0) + sel.length;
    row.hidden = total === 0;
    var clear = row.querySelector('[data-oj-clear]');
    if (clear) clear.hidden = total < 2;
  }

  /* ---------- Events ---------- */

  if (drawer) {
    drawer.addEventListener('change', function (event) {
      var input = event.target;
      if (!input || !input.hasAttribute('data-oj-checkbox')) return;
      draft = readDraftFromInputs();
      confirming = false;
      hideConfirm();
      refreshDraftUI();
      scheduleCount();
    });

    drawer.addEventListener('click', function (event) {
      var target = event.target;
      var toggle = target.closest('[data-oj-group-toggle]');
      if (toggle) {
        var section = toggle.closest('[data-oj-group]');
        setGroupOpen(section, !section.classList.contains('is-open'));
        rememberExpanded();
        return;
      }
      if (target.closest('[data-oj-filter-close]')) { attemptClose(); return; }
      if (target.closest('[data-oj-filter-discard]')) { closeDrawer(); return; }
      if (target.closest('[data-oj-filter-clear]')) {
        checkboxes().forEach(function (input) { input.checked = false; });
        draft = {};
        confirming = false;
        hideConfirm();
        refreshDraftUI();
        scheduleCount();
        return;
      }
      if (target.closest('[data-oj-filter-apply]')) {
        var next = cloneState(draft);
        var replace = pushedHistory;
        closeDrawer({ keepHistory: true, noFocus: true });
        pushedHistory = false;
        track('filter_apply', { values: next, count: lastTotal });
        applyState(next, { replace: replace });
        return;
      }
      if (target.closest('[data-oj-guide-link]')) {
        track('guide_tasting_menu_click', {});
      }
    });

    if (form) {
      form.addEventListener('submit', function (event) { event.preventDefault(); });
    }
  }

  if (backdrop) {
    backdrop.addEventListener('click', function () {
      if (!isMobile()) attemptClose();
    });
  }

  if (results) {
    results.addEventListener('click', function (event) {
      var chip = event.target.closest('[data-oj-chip]');
      if (chip) {
        var param = chip.getAttribute('data-oj-param');
        var value = chip.getAttribute('data-oj-value');
        var next = cloneState(applied);
        if (next[param]) {
          next[param] = next[param].filter(function (v) { return v !== value; });
          if (!next[param].length) delete next[param];
        }
        track('chip_remove', { param: param, value: value });
        applyState(next, {});
        return;
      }
      if (event.target.closest('[data-oj-clear]')) {
        track('chip_remove', { param: 'all', value: 'all' });
        applyState({}, {});
        return;
      }
      var groupMore = event.target.closest('[data-oj-group-more]');
      if (groupMore) {
        var group = groupMore.closest('[data-oj-format-group]');
        if (group) expandGroup(group);
        track('format_show_all', { format: group ? group.getAttribute('data-oj-format-group') : '' });
        return;
      }
      var more = event.target.closest('[data-oj-loadmore]');
      if (more) loadMore(more);
    });
  }

  window.addEventListener('popstate', function () {
    if (drawerOpen) {
      closeDrawer({ fromPopstate: true });
      return;
    }
    var urlState = readState(window.location.search);
    if (stateEquals(urlState, applied)) return;
    applied = cloneState(urlState);
    draft = cloneState(applied);
    renderResults(window.location.pathname + window.location.search, { noFocus: true });
  });

  bindOpeners(doc);

  /* Grouped grid: honour a format selection in the URL on first paint. */
  if (results && results.querySelector('[data-oj-format-group]')) {
    applyFormatView(applied);
    renderFormatChips();
    if (formatSelection(applied).length) {
      updateBar(applied, visibleTotal());
      setCountText(fragrancesLabel(visibleTotal()));
    }
  }

  /* ---------- Analytics: filter_open, filter_apply, chip_remove, guide_tasting_menu_click, format_show_all ----------
     Every event goes to the dataLayer (GTM), to gtag when Google's tag is present,
     and to Klaviyo's tracker when it is loaded, so filtered sessions can be
     compared with the old drawer in any of the three. */

  function flatValues(values) {
    var out = {};
    Object.keys(values || {}).forEach(function (k) {
      out[k.replace(PARAM_PREFIX, '')] = values[k].join(',');
    });
    return out;
  }

  doc.addEventListener('oj:filter:track', function (event) {
    var detail = event.detail || {};
    var name = detail.event;
    var data = detail.data || {};
    var payload = {};
    if (name === 'filter_open') payload.source = data.source;
    if (name === 'filter_apply') { payload.values = flatValues(data.values); payload.count = data.count; }
    if (name === 'chip_remove') { payload.param = (data.param || '').replace(PARAM_PREFIX, ''); payload.value = data.value; }
    if (name === 'format_show_all') payload.format = data.format;
    payload.collection = window.location.pathname;

    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: name, component: 'fragrance_filter' }, payload));
    } catch (e) { /* ignore */ }
    try {
      if (typeof window.gtag === 'function') window.gtag('event', name, Object.assign({ component: 'fragrance_filter' }, payload));
    } catch (e) { /* ignore */ }
    try {
      var kl = window.klaviyo || window._learnq;
      if (kl && typeof kl.push === 'function') kl.push(['track', 'Fragrance filter: ' + name, payload]);
    } catch (e) { /* ignore */ }
  });

  window.OJFilter = {
    open: openDrawer,
    close: closeDrawer,
    apply: applyState,
    state: function () { return cloneState(applied); },
    refreshBar: checkPinned
  };
})();
